import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import Modal from 'react-bootstrap/Modal';

import useAnnouncementModalStore from './state-hook';
import CommonProps from './common/common-props';
import createDisplayedAnnouncement from '../../services/announcement/displayed-announcement-service';

const SModal = styled(Modal)`
  outline: none;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-150%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  & > article.horizontal {
    width: 600px;
    height: fit-content !important;
    border-radius: 5px;
    animation: slideIn 1s cubic-bezier(0.5, -0.01, 0.47, 1.32);
    background-color: transparent;

    ${props => props.theme.isMobileOrTablet && `
      width: 100%;
      max-width: 345px;
      margin: auto;
    `};
  }

  button.close {
    position: absolute;
    right: 0;
    background: transparent;
    border: none;
    outline: none;
    color: white;
    padding: 0;
    margin: -44px -28px 0 0;
    font-size: 48px;

    ${props => props.theme.isMobileOrTablet && `
      margin: -48px 0 0 -24px;
    `};
  }
`;

const Header = styled(Modal.Header)`
  position: relative;
`;

const Body = styled(Modal.Body)`
  display: flex;
  flex-direction: column;
  padding: 40px 48px 48px;
  align-items: center;
  background-color: white;

  ${props => props.theme.isMobileOrTablet && `
    padding: 40px 16px 48px;
  `};
`;

const Hero = styled.img`
  width: 600px;
  border-radius: 5px 5px 0 0;

  ${props => props.theme.isMobileOrTablet && `
    width: 100%;
    max-width: 345px;
  `};
`;

const Headline = styled.div`
  font-size: 24px;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: 900;
  line-height: 120%;
  text-align: center;
  text-transform: uppercase;
`;

const Copy = styled.div`
  font-size: 19px;
  font-family: ${props => props.theme.fontFamilyBody};
  line-height: 120%;
  text-align: center;
  margin-top: 16px;
  padding: 0 8px;
`;

const Button = styled.button`
  height: 40px;
  padding: 0 24px;
  font-size: 16px;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightHeavy};
  line-height: 120%;
  text-transform: uppercase;
  color: ${props => props.theme.bodyBackgroundColor};
  background-color: #3D4A63;
  border: 1px solid #3D4A63;
  border-radius: 2px;
  outline: none;
  margin-top: 40px;
`;

export default function AnnouncementModal({ announcement, preview }) {
  const set = useAnnouncementModalStore(state => state.set);
  const show = useAnnouncementModalStore(state => state.show);
  const currentAnnouncement = useAnnouncementModalStore(
    state => state.currentAnnouncement,
  );

  const {
    id,
    headline,
    copy,
    buttonCopy,
    buttonLink,
    heroImageUrl,
  } = currentAnnouncement;

  React.useEffect(() => {
    set(state => {
      state.currentAnnouncement = announcement;
    });

    if (!preview) {
      set(state => {
        state.show = true;
      });
    }
  }, [announcement, set, preview]);

  const handleShow = () => {
    if (!preview) {
      createDisplayedAnnouncement({ announcement_id: id });
    }
  };

  const handleHide = () => {
    set(state => {
      state.show = false;
    });
  };

  const handleButtonClick = e => {
    e && e.preventDefault();

    set(state => {
      state.show = false;
    });

    if (buttonLink) {
      window.open(buttonLink, '_blank');
    }
  };

  return (
    <ThemeProvider theme={CommonProps()}>
      <SModal
        show={show}
        centered="true"
        dialogAs="article"
        dialogClassName="horizontal"
        onShow={handleShow}
        onHide={handleHide}
        className={show ? '' : 'hide'}
      >
        <Header closeButton />
        <Hero src={heroImageUrl} />
        <Body>
          <Headline>{headline}</Headline>
          <Copy>{copy}</Copy>
          <Button onClick={handleButtonClick}>{buttonCopy}</Button>
        </Body>
      </SModal>
    </ThemeProvider>
  );
}

AnnouncementModal.propTypes = {
  announcement: PropTypes.shape({
    id: PropTypes.number,
    headline: PropTypes.string,
    copy: PropTypes.string,
    buttonCopy: PropTypes.string,
    buttonLink: PropTypes.string,
    heroImageUrl: PropTypes.string,
  }).isRequired,
  preview: PropTypes.bool,
};

AnnouncementModal.defaultProps = {
  preview: false,
};
